import { useContext, useMemo } from 'react';
import { CountryConfigContext } from '../contexts/country-config-context/country-config-context';

/**
 * @typedef {Record<string, string | undefined>} countryConfigData
 */

/**
 * @typedef {object} countryConfig
 * @property {string} name
 * @property {countryConfigData} data
 */

/**
 * @param {Array<countryConfig>} config
 * @param {string} featureName
 * @returns {[boolean, countryConfigData]}
 */
export const getCountryFeatureConfig = (config, featureName) => {
  const selectedConfig = config.find((feature) => featureName === feature.name);
  return [!!selectedConfig, selectedConfig?.data || {}];
};

/**
 * @param {string} featureName
 * @returns {[boolean, countryConfigData]}
 * The first element checks whether the feature exists at all.
 * The second element returns the configuration.
 *
 * It is always of type `Record<string, string | undefined>`.
 *
 * Flags are often placed here, for example:
 * "isPackageRequired": "true".
 *
 * This can be said to be working fine.
 * However, if a flag is created with a value like:
 * "isPackageRequired": "false", the value here is not converted to a boolean anywhere.
 *
 * So, "false" (as a string) will always satisfy the condition,
 * because it is a string, not a boolean.
 *
 * Make sure the flags from here are not of type `string`.
 */

export const useCountryConfig = (featureName) => {
  const { config } = useContext(CountryConfigContext);

  return useMemo(() => getCountryFeatureConfig(config, featureName), [config, featureName]);
};
